import { Button, Dialog } from '@rooming/ui';
import type { ReactNode } from 'react';
import type { RosterEntry } from './api';
import { SEX_TEXT } from './common';

/**
 * The card of one passenger, for the accounts of the backoffice only (administrator, hotel, guide — never a passenger:
 * this component does not exist in the passenger application). It shows what the screen that opens it already has;
 * it asks the server for nothing by itself.
 *
 * The document number is shown only when it was read through the explicit, audited read of the list (hotel and
 * guide). An administrator does not get documents in this view: the server does not hand them out here.
 */
export function PassengerDetailModal({
  passenger,
  bed,
  document,
  documentAction,
  onClose,
}: {
  /** `undefined` = closed. */
  readonly passenger: RosterEntry | undefined;
  /** Where the passenger sleeps, when they have a place. */
  readonly bed: { readonly room: string; readonly position: number } | undefined;
  /** The document number, when this session has already read the documents. */
  readonly document: string | undefined;
  /** What to offer instead of the number: the button of the audited read, or why it is not available. */
  readonly documentAction: ReactNode;
  readonly onClose: () => void;
}) {
  const rows: readonly (readonly [string, ReactNode])[] =
    passenger === undefined
      ? []
      : [
          ['Nombre completo', `${passenger.firstNames} ${passenger.lastNames}`],
          ['Sexo', SEX_TEXT[passenger.sex] ?? passenger.sex],
          ['Documento', document === undefined ? documentAction : <span className="font-mono">{document}</span>],
          ['Estado', passenger.room === null ? 'Sin plaza' : 'Con plaza'],
          [
            'Habitación y cama',
            passenger.room === null
              ? '—'
              : `${passenger.room}${bed === undefined ? '' : ` · cama ${String(bed.position)}`}`,
          ],
          [
            'Habitación del hotel',
            passenger.room === null ? '—' : (passenger.physicalRoom ?? 'Todavía no cargada por el hotel'),
          ],
          // The import does not carry contact data yet (PENDIENTES_PARA_DECIDIR.md A2): nothing is invented.
          ['Contacto', 'Sin datos de contacto cargados'],
        ];
  return (
    <Dialog
      open={passenger !== undefined}
      title="Ficha del pasajero"
      onClose={onClose}
      actions={
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-[auto_1fr]">
        {rows.map(([term, value]) => (
          <div key={term} className="contents">
            <dt className="text-sm font-semibold text-muted">{term}</dt>
            <dd className="text-base break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </Dialog>
  );
}
